import React, { useState, useEffect } from 'react';
import { useIsAuthorized, useLoading, useUserInfo, useImpersonatedUserId, useStation } from '../contexts/GlobalStateContext';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import BattenburgLoader from '../common/BattenburgLoader';
import 'react-toastify/dist/ReactToastify.css';
import { fetchWithAuth, uploadWithAuth, getUserInfoById } from './../services/api';

const DokumentForm = () => {
  const { impersonatedUserId } = useImpersonatedUserId();

  const [documents, setDocuments] = useState({ passtider: [], personal: [], heroma: [] });
  const [user, setUser] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState({ passtider: null, personal: null, heroma: null });
  const [stationer, setStationer] = useState([]);
  const { loading, setLoading } = useLoading();
  const { station, setStation } = useStation();
  const { userInfo } = useUserInfo();
  const { isAuthorized } = useIsAuthorized();

  const isSystemAdmin = userInfo?.role === 'SystemAdmin';

  const fetchDocuments = async (station) => {
    try {
      let url = `/api/documents/list?station=${station}`;
      const response = await fetchWithAuth(url);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Kunde inte hämta filer');
      }

      const data = await response.json();
      setDocuments({
        passtider: data.passtider || [],
        personal: data.personal || [],
        heroma: data.heroma || [],
      });

    } catch (error) {
      console.log('Fetch documents error:', error);
      const msg = error.message || '❌ Kunde inte hämta filer.';
      toast.error(msg);
    }
  };

  const getStationer = async (userId) => {
    try {
      if (!userId) return;
      const url = `/api/stationer/names?userId=${userId}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Kunde inte hämta stationer');
      const data = await response.json();
      setStationer(data.length === 0 ? [] : data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const loadData = async () => {
      if (!isAuthorized) return;
      const userId = impersonatedUserId || userInfo.userId;
      await getStationer(userId);

      if (!station || station === '---') return;
      await fetchDocuments(station);

      if (isSystemAdmin && impersonatedUserId) {
        try {
          const data = await getUserInfoById(impersonatedUserId);
          setUser(data);
        } catch (error) {
          console.error('Fel vid hämtning av användardata:', error);
        }
      }
    };

    loadData();
  }, [impersonatedUserId, isAuthorized, isSystemAdmin, station, userInfo?.userId]);


  const handleFileSelect = (event, category) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedDocument(prev => ({ ...prev, [category]: file }));
  }

  const uploadFile = async (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('station', station);
    formData.append('category', category);
    if (userInfo.role === 'SystemAdmin' && impersonatedUserId) {
      formData.append('impersonatedUserId', impersonatedUserId);
    }

    const response = await uploadWithAuth('/api/documents/upload', formData);
    if (!response.ok) {
      const error = await response.json();
      console.log("uploadFile error: ", error)
      throw new Error(error.message || error || 'Upload failed');
    }

    return await response.json();
  };


  const handleSave = async (category) => {
    const file = selectedDocument[category];
    if (!file) {
      toast.warning('⚠️ Ingen fil vald');
      return;
    }

    setLoading(true);
    try {
      await uploadFile(file, category);

      toast.success(`✅ Fil "${file.name}" uppladdad!`);

      setSelectedDocument(prev => ({ ...prev, [category]: null }));

      const fileInput = document.querySelector(`input[type="file"][data-category="${category}"]`);
      if (fileInput) fileInput.value = '';

      await fetchDocuments(station);

    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`❌ Fel vid uppladdning: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async (category, index, userId) => {
    const file = documents[category][index];
    if (!file) return;

    const fileId = file.id;

    if (!window.confirm(`Är du säker på att du vill radera "${file.name}"?`)) {
      return;
    }

    setLoading(true);
    try {
      let url = `/api/documents/delete/${fileId}`;
      if (userId) {
        url += `?userId=${userId}`;
      }
      const response = await fetchWithAuth(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Delete failed');
      }

      toast.success(`✅ Fil "${file.name}" raderad`);

      setDocuments(prev => ({
        ...prev,
        [category]: prev[category].filter((_, i) => i !== index)
      }));

    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`❌ Fel vid borttagning av fil: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  const renderDocumentsNoFilePicker = (label) => (
    <div className="tw-flex tw-flex-col tw-mb-4">
      <label className="tw-block tw-font-semibold tw-ml-2 tw-mb-1">
        {label}
      </label>
    </div>
  );

  const renderDocumentsFilePickerLeft = (label, category, acceptType) => (
    <div className="tw-flex tw-flex-col tw-mb-4">
      <label htmlFor={`${category}-upload`} className="tw-block tw-font-semibold tw-ml-2 tw-mb-1">
        {label}
      </label>

      <div className="tw-flex tw-items-center tw-gap-2 tw-ml-3">
        <input
          id={`${category}-upload`}
          type="file"
          accept={acceptType}
          data-category={category}
          onChange={(e) => handleFileSelect(e, category)}
          className="tw-min-w-[400px] tw-bg-blue-300 tw-rounded tw-p-1"
        />

        {selectedDocument[category] && (
          <button
            onClick={() => handleSave(category)}
            className="tw-bg-lime-500 tw-text-white tw-px-4 tw-py-1 tw-rounded hover:tw-bg-lime-600 tw-whitespace-nowrap"
          >
            <strong>Spara</strong>
          </button>
        )}
      </div>
    </div>
  );

  const renderDocumentsFilePickerRight = (label, category, acceptType) => (
    <div className="tw-flex tw-flex-col tw-mb-4">
      <label htmlFor={`${category}-upload`} className="tw-block tw-font-semibold tw-mr-2 tw-mb-1 tw-text-right">
        {label}
      </label>

      <div className="tw-flex tw-items-center tw-gap-2 tw-mr-3 tw-flex-row-reverse">
        <input
          id={`${category}-upload`}
          type="file"
          accept={acceptType}
          data-category={category}
          onChange={(e) => handleFileSelect(e, category)}
          className="tw-min-w-[400px] tw-bg-blue-300 tw-rounded tw-p-1"
        />

        {selectedDocument[category] && (
          <button
            onClick={() => handleSave(category)}
            className="tw-bg-lime-500 tw-text-white tw-px-4 tw-py-1 tw-rounded hover:tw-bg-lime-600 tw-whitespace-nowrap"
          >
            <strong>Spara</strong>
          </button>
        )}
      </div>
    </div>
  );

  const renderDocumentTable = (title, category, files) => (
    <div className="tw-border tw-border-black tw-rounded-xl tw-bg-white tw-px-4">
      <div className="tw-overflow-x-auto">
        <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{title}</h3>
        <table className="tw-w-full tw-text-left tw-mb-3">
          <thead className="tw-bg-yellow-300">
            <tr>
              <th className="tw-p-2">Uppdaterad</th>
              <th className="tw-p-2">Filnamn</th>
              <th className="tw-p-2">Åtgärd</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file, index) => (
              <tr key={index} className={index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}>
                <td className="tw-p-2">{file.updated}</td>
                <td className="tw-p-2">
                  <a href={file.url} target="_blank" rel="noopener noreferrer" className="tw-text-blue-600 tw-underline">
                    {file.name}
                  </a>
                </td>
                <td className="tw-p-2">
                  <button
                    onClick={() => handleDelete(category, index, impersonatedUserId)}
                    className="tw-text-red-500 hover:tw-text-red-700 tw-border-0 tw-bg-transparent"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}

            {files.length === 0 && (
              <tr>
                <td colSpan={3} className="tw-p-2 tw-text-gray-500 tw-text-center">
                  Inga filer uppladdade
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-[2600px] tw-mx-auto tw-py-3">
        {isAuthorized ? (
          <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-max-w-full">
            <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
              <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Dokument</h3>
            </div>
            <div className="tw-flex tw-items-center tw-gap-4">
              <div className="tw-min-h-[40px]">
                {loading && (
                  <BattenburgLoader />
                )}
              </div>
              <div className="tw-min-h-[40px]">
                {impersonatedUserId && user && (
                  <div className="tw-mb-2">
                    <h3 className="tw-font-semibold tw-text-lg tw-text-gray-800 tw-text-center">
                      Du agerar som användare:
                      <span className="tw-text-red-500 hover:tw-text-red-700 tw-ml-1">
                        <strong>{user.userName}</strong>
                      </span>
                    </h3>
                  </div>
                )}
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-min-h-[40px]">
              <label htmlFor="stationer-select" className="tw-px-2 tw-ml-3 tw-text-sm tw-font-bold">
                Station:
              </label>
              {stationer.length > 0 && (
                <select
                  id="stationer-select"
                  value={station}
                  disabled={!isAuthorized || loading}
                  onChange={(e) => setStation(e.target.value)}
                  className="tw-min-w-[160px] tw-border tw-p-2 tw-mr-2 tw-text-sm tw-leading-normal tw-font-bold"
                >
                  {station === '---' && <option key='placeholder_station' value="">Välj station</option>}
                  {stationer.map((table) => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="tw-p-6">
              <div className="tw-grid tw-grid-cols-3 tw-gap-6">
                <div>
                  {renderDocumentsFilePickerRight("Passtider och Personallistor:", "passtider", ".xlsx")}
                </div>
                <div>
                  {renderDocumentsNoFilePicker()}
                </div>
                <div>
                  {renderDocumentsFilePickerLeft("Schemadata (Heroma):", "heroma", ".txt")}
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-3 tw-gap-6 tw-mt-6">
                {renderDocumentTable("Passtider", "passtider", documents.passtider)}
                {renderDocumentTable("Personallistor", "personal", documents.personal)}
                {renderDocumentTable("Arbetsscheman (Heroma)", "heroma", documents.heroma)}
              </div>
            </div>
          </div>
        ) : (
          <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-max-w-full">
            <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
              <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Logga in för att se dokumenten</h3>
            </div>
            <div className="tw-p-0"></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DokumentForm;